import React, { useState, useCallback } from 'react'
import { EditableCell } from '../ui/EditableCell'
import { AddRowButton } from '../ui/AddRowButton'
import { AddColumnButton } from '../ui/AddColumnButton'
import { DeleteButton } from '../ui/DeleteButton'
import { ColumnEditModal } from '../ui/ColumnEditModal'
import { ColumnInfo, RowData } from '../../types/database'
import { useEditHistory } from '../../hooks/useEditHistory'

interface EditableTableProps {
  tableName: string
  columns: ColumnInfo[]
  rows: RowData[]
  onDataChange: (tableName: string, rowIndex: number, columnName: string, newValue: any) => void
  onColumnAdd: (tableName: string, columnName: string, columnType: string) => void
  onColumnDelete: (tableName: string, columnName: string) => void
  onRowAdd: (tableName: string) => void
  onRowDelete: (tableName: string, rowIndex: number) => void
  className?: string
}

export const EditableTable: React.FC<EditableTableProps> = ({
  tableName,
  columns,
  rows,
  onDataChange,
  onColumnAdd,
  onColumnDelete,
  onRowAdd,
  onRowDelete,
  className = ''
}) => {
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnName: string } | null>(null)
  const [showColumnModal, setShowColumnModal] = useState(false)
  const { addEdit, editCount } = useEditHistory()

  const handleCellEdit = useCallback((rowIndex: number, columnName: string) => {
    setEditingCell({ rowIndex, columnName })
  }, [])

  const handleCellSave = useCallback((rowIndex: number, columnName: string, newValue: any) => {
    const oldValue = rows[rowIndex]?.[columnName]
    
    // 値が変更された場合のみ履歴に追加
    if (oldValue !== newValue) {
      addEdit({
        tableName,
        rowIndex,
        columnName,
        oldValue,
        newValue,
        type: 'update'
      })
    }

    onDataChange(tableName, rowIndex, columnName, newValue)
    setEditingCell(null)
  }, [rows, addEdit, onDataChange])

  const handleCellCancel = useCallback(() => {
    setEditingCell(null)
  }, [])

  const handleAddColumn = useCallback((columnName: string, columnType: string) => {
    onColumnAdd(tableName, columnName, columnType)
    setShowColumnModal(false)
  }, [tableName, onColumnAdd])

  const handleDeleteColumn = useCallback((columnName: string) => {
    if (confirm(`列 "${columnName}" を削除しますか？`)) {
      onColumnDelete(tableName, columnName)
    }
  }, [tableName, onColumnDelete])

  const handleDeleteRow = useCallback((rowIndex: number) => {
    if (confirm(`行 ${rowIndex + 1} を削除しますか？`)) {
      onRowDelete(tableName, rowIndex)
    }
  }, [tableName, onRowDelete])

  const isEditing = (rowIndex: number, columnName: string) => {
    return editingCell?.rowIndex === rowIndex && editingCell?.columnName === columnName
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <div className="action-panel mb-4">
        <div className="flex items-center space-x-2">
          <span className="badge badge-success">編集済みセル: {editCount}個</span>
        </div>
        <div className="flex items-center space-x-2">
          <AddRowButton onClick={() => onRowAdd(tableName)} />
          <AddColumnButton onClick={() => setShowColumnModal(true)} />
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
        <table className="table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.name} className="relative">
                  <div className="flex items-center space-x-1">
                    <span>{column.name}</span>
                    <span className="text-xs text-gray-500">({column.type})</span>
                    <DeleteButton
                      onClick={() => handleDeleteColumn(column.name)}
                      className="ml-2"
                    />
                  </div>
                </th>
              ))}
              <th className="w-12">
                <span className="text-xs text-gray-500">操作</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {columns.map((column) => (
                  <td key={column.name} className="p-0">
                    <EditableCell
                      value={row[column.name]}
                      columnType={column.type}
                      isEditing={isEditing(rowIndex, column.name)}
                      onEdit={() => handleCellEdit(rowIndex, column.name)}
                      onSave={(newValue) => handleCellSave(rowIndex, column.name, newValue)}
                      onCancel={handleCellCancel}
                      className="w-full h-full"
                    />
                  </td>
                ))}
                <td className="p-2">
                  <DeleteButton
                    onClick={() => handleDeleteRow(rowIndex)}
                    className="text-red-500 hover:text-red-700"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ColumnEditModal
        isOpen={showColumnModal}
        onClose={() => setShowColumnModal(false)}
        onSave={handleAddColumn}
        title="新しい列を追加"
      />
    </div>
  )
} 